/**
 * hudStore - transient local HUD state for the multiplayerUI cluster:
 * banners, notices, overlays, media-control paint state. Distinct from
 * roomStore (projections of SHARED room state) - everything here is
 * this-tab-only presentation state.
 *
 * Writers are multiplayer coordination and the existing presentation/media
 * adapters. React and imperative HUD surfaces are readers.
 */
import { createAppStore } from './createAppStore';

export interface HudNotice {
  /** bumps on every show so an identical text still restarts the timer/paint */
  seq: number;
  text: string;
  isError: boolean;
}

export interface HudState {
  followBanner: { presenterName: string } | null;
  breakoutPremove: { roomId: string | null; roomName: string; moveAt: number } | null;
  /** Single replacing toast - last message wins (imperative-toast parity). */
  notice: HudNotice | null;
  removedOverlay: boolean;
  /** terminal state after a voluntary Leave (Rejoin reloads) */
  leftOverlay: boolean;
  mediaState: { mic: boolean; camera: boolean };
  movementToggleVisible: boolean;
}

function initialHudState(): HudState {
  return {
    followBanner: null,
    breakoutPremove: null,
    notice: null,
    removedOverlay: false,
    leftOverlay: false,
    mediaState: { mic: false, camera: false },
    movementToggleVisible: false,
  };
}

export const hudStore = createAppStore<HudState>(initialHudState());

let noticeSeq = 0;
let noticeTimer: ReturnType<typeof setTimeout> | undefined;
/** How long a moderation notice stays up (imperative-toast parity). */
export const NOTICE_MS = 3500;

export function showNotice(text: string, isError = false): void {
  const seq = ++noticeSeq;
  hudStore.setState({ notice: { seq, text, isError } });
  clearTimeout(noticeTimer);
  noticeTimer = setTimeout(() => {
    hudStore.setState((s) => (s.notice?.seq === seq ? { notice: null } : {}));
  }, NOTICE_MS);
}

export function setFollowBanner(banner: HudState['followBanner']): void {
  hudStore.setState({ followBanner: banner });
}

export function setBreakoutPremove(premove: HudState['breakoutPremove']): void {
  hudStore.setState({ breakoutPremove: premove });
}

export function setRemovedOverlay(shown: boolean): void {
  hudStore.setState({ removedOverlay: shown });
}

export function setMediaState(mediaState: HudState['mediaState']): void {
  hudStore.setState({ mediaState });
}

export function setLeftOverlay(shown: boolean): void {
  hudStore.setState({ leftOverlay: shown });
}

export function setMovementToggleVisible(visible: boolean): void {
  hudStore.setState({ movementToggleVisible: visible });
}

export function resetHudState(): void {
  clearTimeout(noticeTimer);
  noticeTimer = undefined;
  noticeSeq = 0;
  hudStore.setState(initialHudState(), true);
}

/** Reconnect reset that preserves user-confirmed local media preferences. */
export function resetHudRoomState(): void {
  clearTimeout(noticeTimer);
  noticeTimer = undefined;
  hudStore.setState({
    followBanner: null,
    breakoutPremove: null,
    notice: null,
    removedOverlay: false,
    leftOverlay: false,
  });
}
